import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../../components/ui/button'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { useAuth } from '../../contexts/AuthContext'
import FormAssignModal from './FormAssignModal'

export default function FormsList() {
  const navigate = useNavigate()
  const { can } = useAuth()
  const canOpenBuilder = can('admin/form-builder', 'view')
  const [forms, setForms] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [assignForm, setAssignForm] = useState(null)
  const [facingFilter, setFacingFilter] = useState('all')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await authFetch(getLocalApiUrl(canOpenBuilder ? '/api/forms' : '/api/forms?status=published'))
      const j = await res.json()
      if (!res.ok || !j.success) throw new Error(j.error || 'Failed to load forms')
      setForms(j.data || [])
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [canOpenBuilder])

  useEffect(() => { load() }, [load])

  const visible = forms.filter((f) => facingFilter === 'all' || f.facing === facingFilter)

  return (
    <div className="p-6">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <p className="text-sm text-slate-500">
            Internal forms assign to staff. Patron forms send an invite to a selected patron.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canOpenBuilder ? (
            <Button onClick={() => navigate('/admin/form-builder')}>Open Form Builder</Button>
          ) : null}
          <Button variant="outline" onClick={() => navigate('/compliance-safe/forms/records')}>Form records</Button>
          <Button variant="outline" onClick={() => navigate('/compliance-safe/forms/pipeline')}>Open pipeline</Button>
        </div>
      </div>
      <div className="flex gap-1 mb-4">
        {[['all', 'All'], ['internal', 'Internal — assign'], ['patron', 'Patron — send']].map(([v, l]) => (
          <button
            key={v}
            type="button"
            onClick={() => setFacingFilter(v)}
            className={`rounded-full border px-3 py-1 text-xs font-semibold ${facingFilter === v ? 'border-teal-600 bg-teal-50 text-teal-800' : 'border-slate-200 text-slate-600'}`}
          >
            {l}
          </button>
        ))}
      </div>
      {error ? <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div> : null}
      {loading ? <div className="text-sm text-slate-500">Loading…</div> : null}
      <div className="space-y-2">
        {visible.map((f) => (
          <div key={f.id} className="rounded-xl border border-slate-200 bg-white px-4 py-3 flex items-center justify-between gap-3">
            <div>
              <div className="font-semibold text-slate-900">{f.name}</div>
              <div className="text-xs text-slate-500">
                {f.facing === 'patron' ? 'Patron send' : 'Internal assign'}
                {` · ${f.status}`}
                {f.currentVersionNo ? ` · v${f.currentVersionNo}` : ''}
                {` · ${f.assignmentCount || 0} assignments`}
                {` · ${f.submissionCount || 0} submitted`}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {canOpenBuilder ? (
                <Button variant="outline" onClick={() => navigate(`/admin/form-builder/${f.id}`)}>Edit</Button>
              ) : null}
              <Button variant="outline" onClick={() => navigate(`/compliance-safe/forms/${f.id}/records`)}>
                View records
              </Button>
              {canOpenBuilder ? (
                <Button
                  variant="outline"
                  onClick={async () => {
                    setError('')
                    try {
                      const res = await authFetch(getLocalApiUrl(`/api/forms/${f.id}/test`), { method: 'POST' })
                      const j = await res.json()
                      if (!res.ok || !j.success) throw new Error(j.error || 'Could not start a test run')
                      navigate(`/compliance-safe/forms/assignments/${j.data.assignment.id}`)
                    } catch (e) {
                      setError(e.message)
                    }
                  }}
                >
                  Test
                </Button>
              ) : null}
              {f.status === 'published' ? (
                <Button onClick={() => setAssignForm(f)}>{f.facing === 'patron' ? 'Send to patron' : 'Assign to staff'}</Button>
              ) : (
                <span className="self-center text-xs text-slate-500">Draft — publish to assign</span>
              )}
            </div>
          </div>
        ))}
        {!loading && !visible.length ? (
          <div className="text-sm text-slate-500">
            {facingFilter === 'patron'
              ? 'No published patron forms. Create one in Form Builder and set Facing to Patron.'
              : facingFilter === 'internal'
                ? 'No published internal forms.'
                : canOpenBuilder
                  ? (
                    <button type="button" className="text-teal-700 underline" onClick={() => navigate('/admin/form-builder')}>
                      No published forms. Create one in Form Builder
                    </button>
                  )
                  : 'Ask an admin to create one in System & Master Data → Form Builder.'}
          </div>
        ) : null}
      </div>
      {assignForm ? (
        <FormAssignModal
          form={assignForm}
          onClose={() => setAssignForm(null)}
          onDone={() => { setAssignForm(null); navigate('/compliance-safe/forms/pipeline') }}
        />
      ) : null}
    </div>
  )
}
